// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Debris Ops Store (Zustand)
// Manages load tickets, invoices, audit log, photos
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { TABLES, FIELDS, fetchTable, createRecord, updateRecord, deleteRecord } from '../lib/api-switch';
import { useAuthStore } from './authStore';
import type {
  DebrisLoadTicket, DebrisInvoice, DebrisAuditEntry, DebrisPhoto, DebrisRequest,
} from '../types/debris';

// ── Field key shortcuts ──────────────────────────────────────
const FL = FIELDS.debrisLoadTickets;
const FI = FIELDS.debrisInvoices;
const FA = FIELDS.debrisAuditLog;
const FP = FIELDS.debrisPhotos;
const FR = FIELDS.debrisRequests;

const TL = TABLES.debrisLoadTickets;
const TI = TABLES.debrisInvoices;
const TA = TABLES.debrisAuditLog;
const TP = TABLES.debrisPhotos;
const TR = TABLES.debrisRequests;

function getToken() {
  return useAuthStore.getState().token ?? undefined;
}

// ── Safety: strip base64 photo data from stops JSON if it's too large for Ignatius TEXT (65K) ──
function stripStopPhotosIfTooLarge(json: string): string {
  if (!json || json.length < 50000) return json;
  try {
    const stops = JSON.parse(json);
    if (!Array.isArray(stops)) return json;
    const stripped = stops.map((s: Record<string, unknown>) => {
      const out = { ...s };
      delete out.beforePhotoData;
      delete out.afterPhotoData;
      return out;
    });
    return JSON.stringify(stripped);
  } catch { return json; }
}

// ── Notes encoding for fields that don't have Ignatius columns ──
// Stashes pickupStopsJson, measurementMode, unitCount, etc. into the
// notes TEXT field using triple-pipe markers. Decoded on read, encoded
// on write.
const TICKET_META_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;

function decodeTicketNotes(raw: string): { notes: string; meta: Record<string, string> } {
  if (!raw) return { notes: '', meta: {} };
  const m = raw.match(TICKET_META_RE);
  if (!m) return { notes: raw, meta: {} };
  try {
    const meta = JSON.parse(m[1]);
    const notes = raw.replace(TICKET_META_RE, '').trim();
    return { notes, meta };
  } catch {
    return { notes: raw, meta: {} };
  }
}

// ── Row mappers ──────────────────────────────────────────────

function mapTicket(row: Record<string, unknown>): DebrisLoadTicket {
  const rid = Number(row.id) || 0;
  // Legacy: check if notes has encoded meta from before we had real columns
  const rawNotes = (row.notes as string) ?? '';
  const { notes, meta } = decodeTicketNotes(rawNotes);
  return {
    id: rid, Rid: rid,
    incidentId: (row.incidentId as string) ?? '',
    ticketNumber: (row.ticketNumber as string) ?? '',
    truckId: (row.truckId as string) ?? '',
    truckNumber: (row.truckNumber as string) ?? '',
    crewId: (row.crewId as string) ?? '',
    contractorId: (row.contractorId as string) ?? '',
    zoneId: (row.zoneId as string) ?? '',
    disposalSiteId: (row.disposalSiteId as string) ?? '',
    debrisType: (row.debrisType as string) ?? '',
    measurementMode: (row.measurementMode as string) || meta.measurementMode || 'volumetric',
    loadPercentage: (row.loadPercentage as string) ?? '',
    calculatedCubicYards: (row.calculatedCubicYards as string) ?? '',
    unitCount: (row.unitCount as string) || meta.unitCount || '',
    unitSubcategory: (row.unitSubcategory as string) || meta.unitSubcategory || '',
    diameterRange: (row.diameterRange as string) || meta.diameterRange || '',
    weight: (row.weight as string) || meta.weight || '',
    weightUnit: (row.weightUnit as string) || meta.weightUnit || '',
    scaleTicketNumber: (row.scaleTicketNumber as string) || meta.scaleTicketNumber || '',
    pickupLat: (row.pickupLat as string) ?? '',
    pickupLng: (row.pickupLng as string) ?? '',
    pickupAddress: (row.pickupAddress as string) ?? '',
    pickupTime: (row.pickupTime as string) ?? '',
    pickupStopsJson: (row.pickupStopsJson as string) || meta.pickupStopsJson || '',
    disposalTime: (row.disposalTime as string) ?? '',
    loadingMonitorName: (row.loadingMonitorName as string) ?? '',
    loadingMonitorSignature: (row.loadingMonitorSignature as string) ?? '',
    driverPickupSignature: (row.driverPickupSignature as string) ?? '',
    loadingLoadPct: (row.loadingLoadPct as string) ?? '',
    towerLoadPct: (row.towerLoadPct as string) ?? '',
    monitorName: (row.monitorName as string) ?? '',
    monitorSignature: (row.monitorSignature as string) ?? '',
    driverSignature: (row.driverSignature as string) ?? '',
    verifiedAt: (row.verifiedAt as string) || meta.verifiedAt || '',
    handLoaded: (row.handLoaded as string) ?? '',
    qrCode: (row.qrCode as string) || meta.qrCode || '',
    status: (row.status as string) as DebrisLoadTicket['status'] ?? 'draft',
    photoBeforeUrl: (row.photoBeforeUrl as string) ?? '',
    photoAfterUrl: (row.photoAfterUrl as string) ?? '',
    photoLoadUrl: (row.photoLoadUrl as string) ?? '',
    notes,
  };
}

function mapInvoice(row: Record<string, unknown>): DebrisInvoice {
  const rid = Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: (row.incidentId as string) ?? '',
    contractorId: (row.contractorId as string) ?? '',
    invoiceNumber: (row.invoiceNumber as string) ?? '',
    periodStart: (row.periodStart as string) ?? '',
    periodEnd: (row.periodEnd as string) ?? '',
    totalCubicYards: (row.totalCubicYards as string) ?? '',
    totalAmount: (row.totalAmount as string) ?? '',
    ticketIds: (row.ticketIds as string) ?? '[]',
    lineItems: (row.lineItems as string) ?? '[]',
    status: (row.status as string) as DebrisInvoice['status'] ?? 'draft',
    approvedBy: (row.approvedBy as string) ?? '',
    notes: (row.notes as string) ?? '',
  };
}

function mapAudit(row: Record<string, unknown>): DebrisAuditEntry {
  const rid = Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: (row.incidentId as string) ?? '',
    entityType: (row.entityType as string) ?? '',
    entityId: (row.entityId as string) ?? '',
    action: (row.action as string) ?? '',
    fieldChanged: (row.fieldChanged as string) ?? '',
    oldValue: (row.oldValue as string) ?? '',
    newValue: (row.newValue as string) ?? '',
    actorName: (row.actorName as string) ?? '',
    actorEmail: (row.actorEmail as string) ?? '',
    actorRole: (row.actorRole as string) ?? '',
  };
}

function mapPhoto(row: Record<string, unknown>): DebrisPhoto {
  const rid = Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    parentId: (row.parentId as string) ?? '',
    parentType: (row.parentType as string) ?? '',
    incidentId: (row.incidentId as string) ?? '',
    photoUrl: (row.photoUrl as string) ?? '',
    caption: (row.caption as string) ?? '',
    geoLat: (row.geoLat as string) ?? '',
    geoLng: (row.geoLng as string) ?? '',
    takenAt: (row.takenAt as string) ?? '',
    uploadedBy: (row.uploadedBy as string) ?? '',
  };
}

function mapRequest(row: Record<string, unknown>): DebrisRequest {
  const rid = Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: (row.incidentId as string) ?? '',
    assessmentId: (row.assessmentId as string) ?? '',
    address: (row.address as string) ?? '',
    latitude: (row.latitude as string) ?? '',
    longitude: (row.longitude as string) ?? '',
    damageCategory: (row.damageCategory as string) ?? '',
    damageTypes: (row.damageTypes as string) ?? '[]',
    description: (row.description as string) ?? '',
    status: (row.status as string) as DebrisRequest['status'] ?? 'pending',
    taggedBy: (row.taggedBy as string) ?? '',
    taggedAt: (row.taggedAt as string) ?? '',
    actionedBy: (row.actionedBy as string) ?? '',
    actionedAt: (row.actionedAt as string) ?? '',
    createdTicketId: (row.createdTicketId as string) ?? '',
    notes: (row.notes as string) ?? '',
  };
}

// ── Store Interface ──────────────────────────────────────────

interface DebrisOpsStore {
  loadTickets: DebrisLoadTicket[];
  invoices: DebrisInvoice[];
  auditLog: DebrisAuditEntry[];
  photos: DebrisPhoto[];
  requests: DebrisRequest[];
  loadingTickets: boolean;
  loadingInvoices: boolean;
  loadingAudit: boolean;
  loadingPhotos: boolean;
  loadingRequests: boolean;
  error: string | null;

  // Load Tickets
  loadLoadTickets: () => Promise<void>;
  createLoadTicket: (data: Partial<DebrisLoadTicket>) => Promise<DebrisLoadTicket | null>;
  updateLoadTicket: (id: number, data: Partial<DebrisLoadTicket>) => Promise<void>;
  deleteLoadTicket: (id: number) => Promise<void>;

  // Invoices
  loadInvoices: () => Promise<void>;
  createInvoice: (data: Partial<DebrisInvoice>) => Promise<DebrisInvoice | null>;
  updateInvoice: (id: number, data: Partial<DebrisInvoice>) => Promise<void>;
  deleteInvoice: (id: number) => Promise<void>;

  // Requests (Impact → Debris handoff)
  loadRequests: () => Promise<void>;
  createRequest: (data: Partial<DebrisRequest>) => Promise<DebrisRequest | null>;
  updateRequest: (id: number, data: Partial<DebrisRequest>) => Promise<void>;
  deleteRequest: (id: number) => Promise<void>;

  // Audit Log
  loadAuditLog: () => Promise<void>;
  createAuditEntry: (data: Partial<DebrisAuditEntry>) => Promise<void>;

  // Photos
  loadPhotos: () => Promise<void>;
  createPhoto: (data: Partial<DebrisPhoto>) => Promise<DebrisPhoto | null>;
  deletePhoto: (id: number) => Promise<void>;
}

// ── Store Implementation ─────────────────────────────────────

export const useDebrisOpsStore = create<DebrisOpsStore>((set, get) => ({
  loadTickets: [],
  invoices: [],
  auditLog: [],
  photos: [],
  requests: [],
  loadingTickets: false,
  loadingInvoices: false,
  loadingAudit: false,
  loadingPhotos: false,
  loadingRequests: false,
  error: null,

  // ── Load Tickets ──────────────────────────────────────────

  loadLoadTickets: async () => {
    set({ loadingTickets: true, error: null });
    try {
      const data = await fetchTable(TL, { token: getToken() });
      set({ loadTickets: data.map(mapTicket), loadingTickets: false });
    } catch (err) {
      set({ loadingTickets: false, error: err instanceof Error ? err.message : 'Failed to load tickets' });
    }
  },

  createLoadTicket: async (data) => {
    // POST first 10 fields
    const fields: Array<[string, string]> = [
      [FL.incidentId, data.incidentId ?? ''],
      [FL.ticketNumber, data.ticketNumber ?? ''],
      [FL.truckId, data.truckId ?? ''],
      [FL.truckNumber, data.truckNumber ?? ''],
      [FL.crewId, data.crewId ?? ''],
      [FL.contractorId, data.contractorId ?? ''],
      [FL.zoneId, data.zoneId ?? ''],
      [FL.disposalSiteId, data.disposalSiteId ?? ''],
      [FL.debrisType, data.debrisType ?? ''],
      [FL.loadPercentage, data.loadPercentage ?? ''],
    ];
    const rid = await createRecord(TL, fields, getToken());
    if (rid) {
      // PUT batch 2 (max 10)
      const extra1: Array<[string, string]> = [
        [FL.calculatedCubicYards, data.calculatedCubicYards ?? ''],
        [FL.pickupLat, data.pickupLat ?? ''],
        [FL.pickupLng, data.pickupLng ?? ''],
        [FL.pickupAddress, data.pickupAddress ?? ''],
        [FL.pickupTime, data.pickupTime ?? ''],
        [FL.disposalTime, data.disposalTime ?? ''],
        [FL.monitorName, data.monitorName ?? ''],
        [FL.monitorSignature, data.monitorSignature ?? ''],
        [FL.driverSignature, data.driverSignature ?? ''],
        [FL.status, data.status ?? 'draft'],
      ];
      await updateRecord(TL, rid, extra1, getToken());
      // PUT batch 3 — photos + notes + new fields
      const extra2: Array<[string, string]> = [
        [FL.photoBeforeUrl, data.photoBeforeUrl ?? ''],
        [FL.photoAfterUrl, data.photoAfterUrl ?? ''],
        [FL.photoLoadUrl, data.photoLoadUrl ?? ''],
        [FL.notes, data.notes ?? ''],
        [FL.pickupStopsJson, stripStopPhotosIfTooLarge(data.pickupStopsJson ?? '')],
        [FL.measurementMode, data.measurementMode ?? ''],
        [FL.unitCount, data.unitCount ?? ''],
        [FL.unitSubcategory, data.unitSubcategory ?? ''],
        [FL.diameterRange, data.diameterRange ?? ''],
        [FL.weight, data.weight ?? ''],
      ];
      await updateRecord(TL, rid, extra2, getToken());
      // PUT batch 4 — remaining fields
      const extra3: Array<[string, string]> = [
        [FL.weightUnit, data.weightUnit ?? ''],
        [FL.scaleTicketNumber, data.scaleTicketNumber ?? ''],
        [FL.verifiedAt, data.verifiedAt ?? ''],
        [FL.qrCode, data.qrCode ?? ''],
        [FL.loadingMonitorName, data.loadingMonitorName ?? ''],
        [FL.loadingMonitorSignature, data.loadingMonitorSignature ?? ''],
        [FL.driverPickupSignature, data.driverPickupSignature ?? ''],
        [FL.loadingLoadPct, data.loadingLoadPct ?? ''],
        [FL.towerLoadPct, data.towerLoadPct ?? ''],
        [FL.handLoaded, data.handLoaded ?? ''],
      ];
      // Batch 4 has 10+ fields — split if needed
      for (let i = 0; i < extra3.length; i += 10) {
        await updateRecord(TL, rid, extra3.slice(i, i + 10), getToken());
      }
    }
    await get().loadLoadTickets();
    return get().loadTickets.find(t => t.id === rid) ?? null;
  },

  updateLoadTicket: async (id, data) => {
    const fields: Array<[string, string]> = [];
    if (data.ticketNumber !== undefined) fields.push([FL.ticketNumber, data.ticketNumber]);
    if (data.truckId !== undefined) fields.push([FL.truckId, data.truckId]);
    if (data.truckNumber !== undefined) fields.push([FL.truckNumber, data.truckNumber]);
    if (data.crewId !== undefined) fields.push([FL.crewId, data.crewId]);
    if (data.contractorId !== undefined) fields.push([FL.contractorId, data.contractorId]);
    if (data.zoneId !== undefined) fields.push([FL.zoneId, data.zoneId]);
    if (data.disposalSiteId !== undefined) fields.push([FL.disposalSiteId, data.disposalSiteId]);
    if (data.debrisType !== undefined) fields.push([FL.debrisType, data.debrisType]);
    if (data.loadPercentage !== undefined) fields.push([FL.loadPercentage, data.loadPercentage]);
    if (data.calculatedCubicYards !== undefined) fields.push([FL.calculatedCubicYards, data.calculatedCubicYards]);
    if (data.pickupLat !== undefined) fields.push([FL.pickupLat, data.pickupLat]);
    if (data.pickupLng !== undefined) fields.push([FL.pickupLng, data.pickupLng]);
    if (data.pickupAddress !== undefined) fields.push([FL.pickupAddress, data.pickupAddress]);
    if (data.pickupTime !== undefined) fields.push([FL.pickupTime, data.pickupTime]);
    if (data.disposalTime !== undefined) fields.push([FL.disposalTime, data.disposalTime]);
    if (data.monitorName !== undefined) fields.push([FL.monitorName, data.monitorName]);
    if (data.monitorSignature !== undefined) fields.push([FL.monitorSignature, data.monitorSignature]);
    if (data.driverSignature !== undefined) fields.push([FL.driverSignature, data.driverSignature]);
    if (data.status !== undefined) fields.push([FL.status, data.status]);
    if (data.photoBeforeUrl !== undefined) fields.push([FL.photoBeforeUrl, data.photoBeforeUrl]);
    if (data.photoAfterUrl !== undefined) fields.push([FL.photoAfterUrl, data.photoAfterUrl]);
    if (data.photoLoadUrl !== undefined) fields.push([FL.photoLoadUrl, data.photoLoadUrl]);
    if (data.notes !== undefined) fields.push([FL.notes, data.notes]);
    // New fields — always write if present in data
    if (data.pickupStopsJson !== undefined) fields.push([FL.pickupStopsJson, stripStopPhotosIfTooLarge(data.pickupStopsJson)]);
    if (data.measurementMode !== undefined) fields.push([FL.measurementMode, data.measurementMode]);
    if (data.unitCount !== undefined) fields.push([FL.unitCount, data.unitCount]);
    if (data.unitSubcategory !== undefined) fields.push([FL.unitSubcategory, data.unitSubcategory]);
    if (data.diameterRange !== undefined) fields.push([FL.diameterRange, data.diameterRange]);
    if (data.weight !== undefined) fields.push([FL.weight, data.weight]);
    if (data.weightUnit !== undefined) fields.push([FL.weightUnit, data.weightUnit]);
    if (data.scaleTicketNumber !== undefined) fields.push([FL.scaleTicketNumber, data.scaleTicketNumber]);
    if (data.verifiedAt !== undefined) fields.push([FL.verifiedAt, data.verifiedAt]);
    if (data.qrCode !== undefined) fields.push([FL.qrCode, data.qrCode]);
    if (data.loadingMonitorName !== undefined) fields.push([FL.loadingMonitorName, data.loadingMonitorName]);
    if (data.loadingMonitorSignature !== undefined) fields.push([FL.loadingMonitorSignature, data.loadingMonitorSignature]);
    if (data.loadingLoadPct !== undefined) fields.push([FL.loadingLoadPct, data.loadingLoadPct]);
    if (data.towerLoadPct !== undefined) fields.push([FL.towerLoadPct, data.towerLoadPct]);
    if (data.handLoaded !== undefined) fields.push([FL.handLoaded, data.handLoaded]);
    if (data.driverPickupSignature !== undefined) fields.push([FL.driverPickupSignature, data.driverPickupSignature]);
    if (fields.length > 0) {
      // Split into batches of 10 for Ignatius PUT
      for (let i = 0; i < fields.length; i += 10) {
        await updateRecord(TL, id, fields.slice(i, i + 10), getToken());
      }
      await get().loadLoadTickets();
    }
  },

  deleteLoadTicket: async (id) => {
    await deleteRecord(TL, id, getToken());
    set({ loadTickets: get().loadTickets.filter(t => t.id !== id) });
  },

  // ── Invoices ──────────────────────────────────────────────

  loadInvoices: async () => {
    set({ loadingInvoices: true, error: null });
    try {
      const data = await fetchTable(TI, { token: getToken() });
      set({ invoices: data.map(mapInvoice), loadingInvoices: false });
    } catch (err) {
      set({ loadingInvoices: false, error: err instanceof Error ? err.message : 'Failed to load invoices' });
    }
  },

  createInvoice: async (data) => {
    const fields: Array<[string, string]> = [
      [FI.incidentId, data.incidentId ?? ''],
      [FI.contractorId, data.contractorId ?? ''],
      [FI.invoiceNumber, data.invoiceNumber ?? ''],
      [FI.periodStart, data.periodStart ?? ''],
      [FI.periodEnd, data.periodEnd ?? ''],
      [FI.totalCubicYards, data.totalCubicYards ?? ''],
      [FI.totalAmount, data.totalAmount ?? ''],
      [FI.ticketIds, data.ticketIds ?? '[]'],
      [FI.lineItems, data.lineItems ?? '[]'],
      [FI.status, data.status ?? 'draft'],
    ];
    const rid = await createRecord(TI, fields, getToken());
    const extra: Array<[string, string]> = [
      [FI.approvedBy, data.approvedBy ?? ''],
      [FI.notes, data.notes ?? ''],
    ];
    if (rid) await updateRecord(TI, rid, extra, getToken());
    await get().loadInvoices();
    return get().invoices.find(i => i.id === rid) ?? null;
  },

  updateInvoice: async (id, data) => {
    const fields: Array<[string, string]> = [];
    if (data.contractorId !== undefined) fields.push([FI.contractorId, data.contractorId]);
    if (data.invoiceNumber !== undefined) fields.push([FI.invoiceNumber, data.invoiceNumber]);
    if (data.periodStart !== undefined) fields.push([FI.periodStart, data.periodStart]);
    if (data.periodEnd !== undefined) fields.push([FI.periodEnd, data.periodEnd]);
    if (data.totalCubicYards !== undefined) fields.push([FI.totalCubicYards, data.totalCubicYards]);
    if (data.totalAmount !== undefined) fields.push([FI.totalAmount, data.totalAmount]);
    if (data.ticketIds !== undefined) fields.push([FI.ticketIds, data.ticketIds]);
    if (data.lineItems !== undefined) fields.push([FI.lineItems, data.lineItems]);
    if (data.status !== undefined) fields.push([FI.status, data.status]);
    if (data.approvedBy !== undefined) fields.push([FI.approvedBy, data.approvedBy]);
    if (data.notes !== undefined) fields.push([FI.notes, data.notes]);
    if (fields.length > 0) {
      await updateRecord(TI, id, fields, getToken());
      await get().loadInvoices();
    }
  },

  deleteInvoice: async (id) => {
    await deleteRecord(TI, id, getToken());
    set({ invoices: get().invoices.filter(i => i.id !== id) });
  },

  // ── Requests (Impact → Debris) ───────────────────────────

  loadRequests: async () => {
    set({ loadingRequests: true, error: null });
    try {
      const data = await fetchTable(TR, { token: getToken() });
      set({ requests: data.map(mapRequest), loadingRequests: false });
    } catch (err) {
      set({ loadingRequests: false, error: err instanceof Error ? err.message : 'Failed to load requests' });
    }
  },

  createRequest: async (data) => {
    const fields: Array<[string, string]> = [
      [FR.incidentId, data.incidentId ?? ''],
      [FR.assessmentId, data.assessmentId ?? ''],
      [FR.address, data.address ?? ''],
      [FR.latitude, data.latitude ?? ''],
      [FR.longitude, data.longitude ?? ''],
      [FR.damageCategory, data.damageCategory ?? ''],
      [FR.damageTypes, data.damageTypes ?? '[]'],
      [FR.description, data.description ?? ''],
      [FR.status, data.status ?? 'pending'],
      [FR.taggedBy, data.taggedBy ?? ''],
    ];
    const rid = await createRecord(TR, fields, getToken());
    const extra: Array<[string, string]> = [
      [FR.taggedAt, data.taggedAt ?? ''],
      [FR.actionedBy, data.actionedBy ?? ''],
      [FR.actionedAt, data.actionedAt ?? ''],
      [FR.createdTicketId, data.createdTicketId ?? ''],
      [FR.notes, data.notes ?? ''],
    ];
    if (rid) await updateRecord(TR, rid, extra, getToken());
    await get().loadRequests();
    return get().requests.find(r => r.id === rid) ?? null;
  },

  updateRequest: async (id, data) => {
    const fields: Array<[string, string]> = [];
    if (data.status !== undefined) fields.push([FR.status, data.status]);
    if (data.actionedBy !== undefined) fields.push([FR.actionedBy, data.actionedBy]);
    if (data.actionedAt !== undefined) fields.push([FR.actionedAt, data.actionedAt]);
    if (data.createdTicketId !== undefined) fields.push([FR.createdTicketId, data.createdTicketId]);
    if (data.notes !== undefined) fields.push([FR.notes, data.notes]);
    if (fields.length > 0) {
      await updateRecord(TR, id, fields, getToken());
      await get().loadRequests();
    }
  },

  deleteRequest: async (id) => {
    await deleteRecord(TR, id, getToken());
    set({ requests: get().requests.filter(r => r.id !== id) });
  },

  // ── Audit Log ─────────────────────────────────────────────

  loadAuditLog: async () => {
    set({ loadingAudit: true, error: null });
    try {
      const data = await fetchTable(TA, { token: getToken() });
      set({ auditLog: data.map(mapAudit), loadingAudit: false });
    } catch (err) {
      set({ loadingAudit: false, error: err instanceof Error ? err.message : 'Failed to load audit log' });
    }
  },

  createAuditEntry: async (data) => {
    const fields: Array<[string, string]> = [
      [FA.incidentId, data.incidentId ?? ''],
      [FA.entityType, data.entityType ?? ''],
      [FA.entityId, data.entityId ?? ''],
      [FA.action, data.action ?? ''],
      [FA.fieldChanged, data.fieldChanged ?? ''],
      [FA.oldValue, data.oldValue ?? ''],
      [FA.newValue, data.newValue ?? ''],
      [FA.actorName, data.actorName ?? ''],
      [FA.actorEmail, data.actorEmail ?? ''],
      [FA.actorRole, data.actorRole ?? ''],
    ];
    await createRecord(TA, fields, getToken());
  },

  // ── Photos ────────────────────────────────────────────────

  loadPhotos: async () => {
    set({ loadingPhotos: true, error: null });
    try {
      const data = await fetchTable(TP, { token: getToken() });
      set({ photos: data.map(mapPhoto), loadingPhotos: false });
    } catch (err) {
      set({ loadingPhotos: false, error: err instanceof Error ? err.message : 'Failed to load photos' });
    }
  },

  createPhoto: async (data) => {
    const fields: Array<[string, string]> = [
      [FP.parentId, data.parentId ?? ''],
      [FP.parentType, data.parentType ?? ''],
      [FP.incidentId, data.incidentId ?? ''],
      [FP.photoUrl, data.photoUrl ?? ''],
      [FP.caption, data.caption ?? ''],
      [FP.geoLat, data.geoLat ?? ''],
      [FP.geoLng, data.geoLng ?? ''],
      [FP.takenAt, data.takenAt ?? ''],
      [FP.uploadedBy, data.uploadedBy ?? ''],
    ];
    const rid = await createRecord(TP, fields, getToken());
    await get().loadPhotos();
    return get().photos.find(p => p.id === rid) ?? null;
  },

  deletePhoto: async (id) => {
    await deleteRecord(TP, id, getToken());
    set({ photos: get().photos.filter(p => p.id !== id) });
  },
}));
